import {
    CustomPagePathDescriptionVisibility,
    CustomPageVisibility,
    DataBindingMode,
    IConfigControlMeasure,
    IControlType,
    IMeasureControl,
    isDataBindingProperty,
    isJsonObject,
    IVirtualPanelItem,
} from '@se/cp.common';
import { DataBinding, Guid } from '@se/se.ui.html5';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { CpModelService } from '../../cp-model.service';
import { CpControl } from './cp-control';
import { ICpEntity, isEntity, PropertyType } from './cp-entity';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { isNotNullOrUndefined } from '../utils/utils';
import { first } from 'lodash-es';

export interface NumericValues {
    strModel: string | null;
    numModel: number | null;
    separator: string | null;
}

const TOPOGRAPHY_DATABINDING = new DataBinding('IdUsMeasureTopography.Description');
const LATERALITY_DATABINDING = new DataBinding('IdUsMeasureLaterality.Description');
const DETAIL_DATABINDING = new DataBinding('Detail');
const METHOD_DATABINDING = new DataBinding('IdUsMeasureMethod.Description');

export class CpMeasureControl extends CpControl {
    readonly controlTypeGroup = 'Measure';
    readonly config: IConfigControlMeasure;
    dataBinding: DataBinding;
    readonly: boolean;
    required: boolean;
    conversionFactor?: number;
    decimalDigits?: number;
    formulaDescription?: string;
    formulaReference?: string;
    readonly _heightHint: number;

    private modelService!: CpModelService;

    private minNormalValue$ = new BehaviorSubject<number | undefined>(undefined);
    private maxNormalValue$ = new BehaviorSubject<number | undefined>(undefined);
    private normalityReferenceValue$ = new BehaviorSubject<number | undefined>(undefined);

    private isAlive$ = new Subject<void>();

    constructor(control: IMeasureControl, readonly context?: DataBinding) {
        super(control);
        this.config = control.config[0];
        this.readonly = this.config.readOnly;
        this.required = this.config.required;

        this.dataBinding = new DataBinding(this.config.dataBinding, context);
        this._heightHint = this.calculateHeight();
    }

    get detailsHeader$() {
        return this.getEntityObservable().pipe(
            takeUntil(this.isAlive$),
            filter(isNotNullOrUndefined),
            map((measure) => {
                const idUsMeasure = measure.getPropertyValue('IdUsMeasure');
                if (!isEntity(idUsMeasure)) {
                    return '';
                }

                const site = idUsMeasure.getPropertyValue('SiteDescription')?.toString() ?? '';
                const topography = measure.getValue(TOPOGRAPHY_DATABINDING) ?? '';
                const laterality = measure.getValue(LATERALITY_DATABINDING) ?? '';
                const measureName = this.name;
                const shownInfo = isDataBindingProperty(this.config.shownInfo)
                    ? measure.getValue(new DataBinding(this.config.shownInfo.propertyName))?.toString() ?? ''
                    : this.config.shownInfo;
                const method = isDataBindingProperty(this.config.method)
                    ? measure.getValue(new DataBinding(this.config.method.propertyName))?.toString() ?? ''
                    : this.config.method;
                const methodFromDetails =
                    first(
                        measure
                            .getAllEntities(DETAIL_DATABINDING)
                            ?.filter((detail) => detail.getPropertyValue('UsedForCalc')),
                    )?.getValue(METHOD_DATABINDING) ?? '';

                return `${site} ${topography} ${laterality}: ${measureName} ${
                    shownInfo || methodFromDetails || method
                }`;
            }),
        );
    }

    setModelService(modelService: CpModelService) {
        this.modelService = modelService;
    }

    canAlignToBottom() {
        switch (this.controlType) {
            case IControlType.Image:
            case IControlType.Spacer:
                return true;
            default:
                return false;
        }
    }

    getHeightHint() {
        return this._heightHint;
    }

    getEntityObservable() {
        return this.modelService.registerEntity(this.dataBinding.entity);
    }

    getInitialValue(propertyName?: string): PropertyType {
        const _propertyName = propertyName ?? this.dataBinding.propertyName;
        return (
            this.modelService.getEntity(this.dataBinding.entity, true)?.getValue(new DataBinding(_propertyName)) ?? null
        );
    }

    getIcon(): string {
        return this.config.icon || 'edit_note';
    }

    getModelObservable(modelService: CpModelService) {
        /*@TODO se non avanzato bisogna sottoscrivere come nel componente SingleDataBindingControl???*/
        return modelService.registerEntity(this.dataBinding.entity).pipe(
            map((model) => model?.getPropertyValue(this.dataBinding.propertyName)),
            distinctUntilChanged(),
        );
    }

    onModelChange(modelService: CpModelService, model: PropertyType, emitEvent = true) {
        return modelService.setValue(this.dataBinding, model, { emitEvent });
    }

    destroyCpComponent() {
        this.isAlive$.next();
        this.isAlive$.complete();
    }

    retrieveConversionFactor(): number {
        const conversionFactor = this.config.conversionFactor;
        return isDataBindingProperty(conversionFactor)
            ? (this.getInitialValue(conversionFactor.propertyName) as number) ?? 1
            : conversionFactor ?? 1;
    }

    retrieveDecimalDigits(): number {
        const decimalDigits = this.config.decimalDigits;
        return isDataBindingProperty(decimalDigits)
            ? (this.getInitialValue(decimalDigits.propertyName) as number) ?? 0
            : decimalDigits ?? 0;
    }

    retrieveFormulaDescription(): string | undefined {
        const formulaDescription = this.config.formulaDescription;
        return isDataBindingProperty(formulaDescription)
            ? (this.getInitialValue(formulaDescription.propertyName) as string) ?? undefined
            : formulaDescription ?? undefined;
    }

    retrieveFormulaReference(): string | undefined {
        const formulaReference = this.config.formulaReference;
        return isDataBindingProperty(formulaReference)
            ? (this.getInitialValue(formulaReference.propertyName) as string) ?? undefined
            : formulaReference ?? undefined;
    }

    retrieveMinNormalValue(entitySubject: Observable<ICpEntity | undefined>): Observable<number | undefined> {
        const minNormalValue = this.config.minNormalValue;
        if (isDataBindingProperty(minNormalValue)) {
            entitySubject
                .pipe(
                    takeUntil(this.isAlive$),
                    map((e) => {
                        const value = e?.getValue(new DataBinding(minNormalValue.propertyName));
                        if (typeof value === 'number') {
                            return value;
                        }
                        return undefined;
                    }),
                )
                .subscribe((value) => this.minNormalValue$.next(value));
        } else {
            this.minNormalValue$.next(minNormalValue ?? undefined);
        }
        return this.minNormalValue$.asObservable();
    }

    retrieveMaxNormalValue(entitySubject: Observable<ICpEntity | undefined>): Observable<number | undefined> {
        const maxNormalValue = this.config.maxNormalValue;
        if (isDataBindingProperty(maxNormalValue)) {
            entitySubject
                .pipe(
                    takeUntil(this.isAlive$),
                    map((e) => {
                        const value = e?.getValue(new DataBinding(maxNormalValue.propertyName));
                        if (typeof value === 'number') {
                            return value;
                        }
                        return undefined;
                    }),
                )
                .subscribe((value) => this.maxNormalValue$.next(value ?? undefined));
        } else {
            this.maxNormalValue$.next(maxNormalValue ?? undefined);
        }
        return this.maxNormalValue$.asObservable();
    }

    retrieveNormalityReferenceValue(entitySubject: Observable<ICpEntity | undefined>): Observable<number | undefined> {
        const normalityReferenceValue = this.config.normalityReferenceValue;
        if (isDataBindingProperty(normalityReferenceValue)) {
            entitySubject
                .pipe(
                    takeUntil(this.isAlive$),
                    map((e) => e?.getValue(new DataBinding(normalityReferenceValue.propertyName)) as number),
                )
                .subscribe((value) => this.normalityReferenceValue$.next(value ?? undefined));
        } else {
            this.normalityReferenceValue$.next(normalityReferenceValue ?? undefined);
        }
        return this.normalityReferenceValue$.asObservable();
    }

    retrieveUnitOfMeasureDisplayed(): string {
        const unitOfMeasureDisplayed = this.config.unitOfMeasureDisplayed;
        return isDataBindingProperty(unitOfMeasureDisplayed)
            ? (this.getInitialValue(unitOfMeasureDisplayed.propertyName) as string) ?? ''
            : unitOfMeasureDisplayed ?? '';
    }

    retrieveShownInfo(): string {
        const shownInfo = this.config.shownInfo;
        return isDataBindingProperty(shownInfo)
            ? (this.getInitialValue(shownInfo.propertyName) as string) ?? undefined
            : shownInfo ?? '';
    }

    retrieveMeasureDescription(): string {
        const description = this.config.description;
        return isDataBindingProperty(description)
            ? (this.getInitialValue(description.propertyName) as string) ?? undefined
            : description ?? '';
    }

    retrieveAcquisitionDate(): string {
        const acquisitionDate = this.config.acquisitionDate;
        return isDataBindingProperty(acquisitionDate)
            ? (this.getInitialValue(acquisitionDate.propertyName) as string) ?? undefined
            : acquisitionDate ?? '';
    }

    makeControlReadOnly() {
        this.readonly = true;
    }

    createDetailsVirtualPanel(): IVirtualPanelItem {
        return {
            controlPanel: {
                columns: 1,
                controls: [
                    {
                        column: 0,
                        columnSpan: 1,
                        row: 0,
                        rowSpan: 1,
                        config: [
                            {
                                id: 'idUserControlMeasureDetails',
                                parameters: {},
                            },
                        ],
                        controlType: IControlType.UserControl,
                        id: new Guid().toString(),
                        isWithLabel: this.isWithLabel,
                        name: 'Measure Details',
                    },
                ],
                rows: 1,
                userControl: null,
            },
            detailPanelItems: [],
            dynamicPanelItems: [],
            header: '',
            id: new Guid().toString(),
            pathDescriptionVisibility: CustomPagePathDescriptionVisibility.hidden,
            virtualPanel: {
                dataBinding: this.dataBinding.entity.toString(),
                dataBindingMode: DataBindingMode.OnRequest,
                idDataSource: '',
                readonly: false,
            },
            visibility: CustomPageVisibility.visible,
        };
    }

    static createDefault(entity: ICpEntity, isPediatric: boolean = false): CpMeasureControl {
        let configAddon: any = {};
        if (isPediatric) {
            configAddon = {
                additionalInformation: 'ZScoreRangeMin/Max',
                maxNormalValue: { propertyName: 'ZScoreRangeMax' },
                minNormalValue: { propertyName: 'ZScoreRangeMin' },
                normalityReferenceValue: { propertyName: 'ZScoreValue' },
            };
        }
        const measure = entity.getPropertyPrimitiveValue('IdUsMeasure');
        const name = isJsonObject(measure) && !!measure.Description ? String(measure.Description) : '';
        const dataBinding = CpMeasureControl.createControlBinding(entity);
        const mockData: IMeasureControl = {
            id: new Guid().toString(),
            name,
            controlType: IControlType.Measure,
            row: 0,
            column: 0,
            rowSpan: 1,
            columnSpan: 1,
            isWithLabel: false,
            constraints: [],
            config: [
                {
                    dataBinding,
                    readOnly: true,
                    required: false,
                    tooltip: '',
                    icon: '',
                    additionalInformation: '',
                    conversionFactor: {
                        propertyName: 'IdUsMeasure.ConvertionFactor',
                    },
                    decimalDigits: { propertyName: 'IdUsMeasure.DecimalDigit' },
                    detailsEnabled: true,
                    formulaDescription: {
                        propertyName: 'IdUsMeasure.FormulaDescription',
                    },
                    formulaReference: {
                        propertyName: 'IdUsMeasure.FormulaReference',
                    },
                    maxNormalValue: { propertyName: 'NormalMaxValue' },
                    minNormalValue: { propertyName: 'NormalMinValue' },
                    normalityReferenceValue: null,
                    unitOfMeasureDisplayed: {
                        propertyName: 'IdUsMeasure.MeasureUnitDisplayDescription',
                    },
                    shownInfo: { propertyName: 'IdUsMeasure.ShownInfo' },
                    mode: { propertyName: 'IdUsMeasure.ModeDescription' },
                    method: { propertyName: 'IdUsMeasure.MethodDescription' },
                    ...configAddon,
                },
            ],
        };
        return new CpMeasureControl(mockData);
    };

    // TODO: Andrea/Michele ???
    private static createControlBinding(entity: ICpEntity): string {
        const rgInteger = /^\d*$/;
        return (
            entity.path
                ?.toString()
                .split('.')
                .filter((item) => !rgInteger.test(item))
                .join('.') + '.Value'
        );
    }

    private calculateHeight() {
        return 66.5;
    }
}
